import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Celebration } from "@/components/Celebration";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

const COPY = {
  speed_pass: ["Clapham Speed Pass active", "Unlimited vibe checks and messages, renewing monthly. Manage auto-renew on your Premium page."],
  vibe_pack: ["5 Speed Tokens added", "Each Speed Token unlocks 1 extra vibe check + 10 messages, or a contact card swap. Go get matching!"],
};

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refresh } = useAuth();
  const qc = useQueryClient();
  const [state, setState] = useState({ status: "checking", product: null });

  useEffect(() => {
    let tries = 0, stop = false;
    const poll = async () => {
      if (stop) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ status: "paid", product: data.product });
          await refresh(); qc.invalidateQueries();
          return;
        }
        if (["expired", "failed"].includes(data.payment_status)) { setState({ status: "failed", product: data.product }); return; }
      } catch { /* retry */ }
      if (++tries < 15) setTimeout(poll, 2000); else setState((s) => ({ ...s, status: "timeout" }));
    };
    if (sessionId) poll(); else setState({ status: "failed" });
    return () => { stop = true; };
  }, [sessionId, refresh, qc]);

  const [title, sub] = COPY[state.product] || ["Payment complete", "Thanks for supporting MoYo Stars Date."];
  return (
    <div data-testid="payment-success-page" className="min-h-screen flex items-center justify-center px-5">
      <Celebration show={state.status === "paid"} onDone={() => {}} />
      <div className="glass rounded-3xl p-8 max-w-sm w-full text-center">
        {state.status === "checking" && <><Loader2 className="h-10 w-10 animate-spin text-amber-300 mx-auto" /><p data-testid="payment-status" className="mt-4 font-display font-bold text-xl">Confirming your payment…</p></>}
        {state.status === "paid" && <><CheckCircle2 className="h-12 w-12 text-emerald-400 mx-auto" /><p data-testid="payment-status" className="mt-4 font-display font-black text-2xl">{title}</p><p className="text-sm text-slate-300 mt-2">{sub}</p></>}
        {["failed", "timeout"].includes(state.status) && <><XCircle className="h-12 w-12 text-pink-400 mx-auto" /><p data-testid="payment-status" className="mt-4 font-display font-bold text-xl">{state.status === "timeout" ? "Still processing" : "Payment not completed"}</p><p className="text-sm text-slate-300 mt-2">{state.status === "timeout" ? "Your payment is taking longer than usual — refresh in a minute." : "No money was taken. You can try again any time."}</p></>}
        <Link to="/discover" data-testid="payment-continue" className="btn-gold rounded-full h-12 mt-6 flex items-center justify-center font-semibold">Back to Discover</Link>
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div data-testid="payment-cancel-page" className="min-h-screen flex items-center justify-center px-5">
      <div className="glass rounded-3xl p-8 max-w-sm w-full text-center">
        <XCircle className="h-12 w-12 text-slate-400 mx-auto" />
        <p className="mt-4 font-display font-bold text-xl">Checkout cancelled</p>
        <p className="text-sm text-slate-300 mt-2">No payment was taken. Your free allowance resets at midnight (London).</p>
        <Link to="/premium" data-testid="payment-retry" className="btn-gold rounded-full h-12 mt-6 flex items-center justify-center font-semibold">See plans</Link>
        <Link to="/discover" className="block text-sm text-slate-400 mt-3 hover:text-white">Back to Discover</Link>
      </div>
    </div>
  );
}
